import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, LogOut } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { DfeLogo } from "@/components/Nav";
import { Field, TextInput } from "@/components/admin/ui";
import { EnquiriesPanel, SettingsPanel } from "@/components/admin/AdminInbox";
import { ProjectsPanel, ServicesPanel, TeamPanel } from "@/components/admin/AdminContent";
import { EventsPanel } from "@/components/admin/AdminEvents";
import { PartnersPanel } from "@/components/admin/AdminPartners";
import { SubscribersPanel } from "@/components/admin/AdminSubscribers";

const Login = ({ onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onSuccess(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm border border-white/10 bg-[#0a0a0a] p-8" data-testid="admin-login-form">
        <div className="flex items-center gap-3 mb-8">
          <DfeLogo size={40} />
          <div>
            <div className="font-display text-xl text-white tracking-wider">DFE ADMIN</div>
            <div className="font-mono2 text-[9px] text-[#a1a1aa] tracking-[0.3em]">後台管理系統</div>
          </div>
        </div>
        <div className="space-y-4">
          <Field label="邮箱 EMAIL">
            <TextInput type="email" required data-testid="admin-login-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@dfemalaysia.com" />
          </Field>
          <Field label="密码 PASSWORD">
            <TextInput type="password" required data-testid="admin-login-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
          </Field>
        </div>
        {error && (
          <p className="font-zh text-sm text-red-400 mt-4" data-testid="admin-login-error">
            {error}
          </p>
        )}
        <button type="submit" disabled={busy} className="btn-yellow w-full justify-center mt-6 disabled:opacity-50" data-testid="admin-login-submit">
          {busy ? "登录中…" : "登录"}
        </button>
        <a href="/" className="block text-center font-mono2 text-[10px] text-[#52525b] hover:text-[#ffde00] tracking-[0.3em] mt-6">
          ← BACK TO SITE
        </a>
      </form>
    </div>
  );
};

const TABS = [
  ["enquiries", "咨询联络"],
  ["projects", "演出项目"],
  ["events", "演出日历"],
  ["subscribers", "订阅观众"],
  ["partners", "合作伙伴"],
  ["services", "服务项目"],
  ["team", "团队成员"],
  ["settings", "联系设置"],
];

export default function Admin() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("enquiries");

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p className="font-mono2 text-xs text-[#a1a1aa] tracking-[0.3em]">LOADING…</p>
      </div>
    );
  }
  if (user === false) return <Login onSuccess={setUser} />;

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 sticky top-0 z-50 bg-[#050505]/95 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <DfeLogo size={32} />
            <span className="font-mono2 text-[10px] text-[#a1a1aa] tracking-[0.3em]">DFE ADMIN · {user.email}</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="font-zh text-sm text-[#a1a1aa] hover:text-[#ffde00] transition-colors">
              查看网站
            </a>
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 font-zh text-sm text-[#a1a1aa] hover:text-red-400 transition-colors bg-transparent border-0 cursor-pointer">
              <LogOut size={15} /> 退出
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 md:px-8 flex gap-1 overflow-x-auto">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`admin-tab-${key}`}
              className={`px-5 py-3 font-zh text-sm whitespace-nowrap border-0 border-b-2 bg-transparent cursor-pointer transition-colors ${
                tab === key ? "text-[#ffde00] border-[#ffde00]" : "text-[#a1a1aa] border-transparent hover:text-white"
              }`}
            >
              {label}
              {key === "enquiries" && <Mail size={12} className="inline ml-2 -mt-0.5" />}
            </button>
          ))}
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        {tab === "enquiries" && <EnquiriesPanel />}
        {tab === "projects" && <ProjectsPanel />}
        {tab === "events" && <EventsPanel />}
        {tab === "subscribers" && <SubscribersPanel />}
        {tab === "partners" && <PartnersPanel />}
        {tab === "services" && <ServicesPanel />}
        {tab === "team" && <TeamPanel />}
        {tab === "settings" && <SettingsPanel />}
      </main>
    </div>
  );
}
